/**
 * Editor de un post con vista previa tipo landing (humano + Claude in Chrome).
 * El HTML rico se guarda tal cual: el modo bloques no está disponible si destruiría el artículo.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, CornerUpRight, ExternalLink, ImageIcon, Loader2, Save } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    useBlogPost,
    useBlogPostsList,
    useBlogTypes,
    useCreateBlogPost,
    useUpdateBlogPost,
} from './hooks/useBlogAdmin';
import {
    EMPTY_BLOG_POST_FORM,
    type BlogLocale,
    type BlogPostFormData,
    type BlogPostListItem,
    type BlogPostStatus,
} from './types';
import { useBlogBlockEditor } from './blocks';
import { isRichBlogHtml } from './lib/isRichBlogHtml';
import { patchHasInternalLeak, textHasDraftMarkers } from './lib/editorialLeak';
import { publicBlogPath, publicBlogUrl, resolveBlogImageUrl } from './lib/resolveBlogImageUrl';
import { buildBlogAiPrompt } from './lib/blogPostTemplate';
import {
    BLOG_SAVE_SUCCESS_MESSAGE,
    validatePublishRequiresMainImage,
} from './lib/blogPostSaveValidation';
import {
    BLOG_QUALITY_PUBLISH_LOG,
    blogQualityPublishRecord,
    rewriteAzureFdImagesToBlob,
    shouldAskPublishConfirm,
} from './lib/blogQualityChecks';
import { BLOG_STATUS_HINTS, BLOG_STATUS_LABELS, BLOG_STATUS_ORDER, editorStatusValue } from './lib/statusLabels';
import { BlogCompleteness } from './components/BlogCompleteness';
import { BlogQualityPanel } from './components/BlogQualityPanel';
import { useBlogPostQuality } from './hooks/useBlogPostQuality';
import { BlogStatusBadge } from './components/BlogStatusBadge';
import { BlogPostPreview } from './components/BlogPostPreview';
import { BlogBlocksPanel } from './components/BlogBlocksPanel';
import { ImagePickerModal } from './components/ImagePickerModal';
import { BLOG_RETIRE_PIPELINE_WARNING, RetireBlogPostDialog } from './components/RetireBlogPostDialog';
import { BLOG_RETIRE_AND_REDIRECT_ENABLED } from './lib/blogRetireFlag';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const inputClass = 'w-full px-3 py-2 rounded-lg border border-border bg-background text-sm';

function oppositeLocale(locale: BlogLocale): BlogLocale {
    return locale === 'es' ? 'en' : 'es';
}

function useDebounced(value: string, ms: number): string {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = window.setTimeout(() => setDebounced(value), ms);
        return () => window.clearTimeout(t);
    }, [value, ms]);
    return debounced;
}

function errMsg(e: unknown): string {
    if (e instanceof Error) return e.message;
    if (e && typeof e === 'object' && 'message' in e) return String((e as { message: string }).message);
    return String(e);
}

function slugFromTitle(title: string): string {
    return title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
}

function completenessFromForm(form: BlogPostFormData) {
    return {
        hasExcerpt: Boolean(form.excerpt.trim()),
        hasBody: form.body_html.trim().length >= 200,
        hasImage: Boolean(form.main_image_url.trim()),
        hasSeo: Boolean(form.meta_title.trim()),
    };
}

export function BlogPostEditorPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = !id || id === 'new';
    const validId = !isNew && UUID_RE.test(id);
    const detailQ = useBlogPost(validId ? id : null);
    const typesQ = useBlogTypes();
    const createMut = useCreateBlogPost();
    const updateMut = useUpdateBlogPost();
    const [form, setForm] = useState<BlogPostFormData>(EMPTY_BLOG_POST_FORM);
    const [bodyMode, setBodyMode] = useState<'html' | 'blocks'>('html');
    const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
    const [copied, setCopied] = useState(false);
    const [showImagePicker, setShowImagePicker] = useState(false);
    const [imagePickerTarget, setImagePickerTarget] = useState<'main' | string>('main');
    const [publishArmed, setPublishArmed] = useState(false);
    const [retireOpen, setRetireOpen] = useState(false);
    const [minorEditorialChange, setMinorEditorialChange] = useState(false);
    const [translationSearch, setTranslationSearch] = useState('');
    const [translationLabel, setTranslationLabel] = useState('');
    const blockEditor = useBlogBlockEditor('');
    const opposite = oppositeLocale(form.locale);
    const debouncedTranslationSearch = useDebounced(translationSearch, 250);
    const translationListQ = useBlogPostsList({
        statusFilter: 'all',
        typeId: null,
        search: debouncedTranslationSearch,
        locale: opposite,
        limit: 12,
    });

    useEffect(() => {
        try {
            sessionStorage.setItem('admin.blog.activeTab', 'posts');
        } catch { /* ignore */ }
    }, []);

    useEffect(() => {
        if (isNew) {
            setForm(EMPTY_BLOG_POST_FORM);
            setBodyMode('html');
            setTranslationSearch('');
            setTranslationLabel('');
            blockEditor.syncFromHtml('');
            return;
        }
        const d = detailQ.data;
        if (!d) return;
        setMinorEditorialChange(false);
        setForm({
            type_id: d.type_id,
            slug: d.slug,
            title: d.title,
            excerpt: d.excerpt ?? '',
            body_html: d.body_html ?? '',
            main_image_url: d.main_image_url ?? '',
            main_image_position: d.main_image_position ?? 'top',
            status: editorStatusValue(d.status),
            meta_title: d.meta_title ?? '',
            meta_description: d.meta_description ?? '',
            og_image_url: d.og_image_url ?? '',
            locale: d.locale === 'en' ? 'en' : 'es',
            translation_of_id: d.translation_of_id ?? null,
            hero_credit_username: d.hero_credit_username ?? '',
        });
        setTranslationSearch('');
        setTranslationLabel(d.translation_of_id ? 'Artículo vinculado' : '');
        const rich = Boolean(d.is_rich) || isRichBlogHtml(d.body_html);
        setBodyMode('html');
        if (!rich) blockEditor.syncFromHtml(d.body_html ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sync once per loaded post
    }, [isNew, detailQ.data]);

    const set = (key: keyof BlogPostFormData) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
            setForm((prev) => ({ ...prev, [key]: e.target.value }));

    const rich = isRichBlogHtml(form.body_html);
    const previewForm = useMemo<BlogPostFormData>(() => {
        if (bodyMode === 'blocks') return { ...form, body_html: blockEditor.toHtml() };
        return form;
    }, [form, bodyMode, blockEditor]);

    const flags = completenessFromForm(previewForm);
    const quality = useBlogPostQuality({
        title: previewForm.title,
        metaTitle: previewForm.meta_title,
        metaDescription: previewForm.meta_description,
        excerpt: previewForm.excerpt,
        bodyHtml: previewForm.body_html,
        currentSlug: previewForm.slug,
    });
    const blockingKey = quality.report.blocking
        .map((item) => `${item.rule}:${item.details.join('|')}`)
        .join('\n');

    useEffect(() => {
        setPublishArmed(false);
    }, [blockingKey, form.status]);
    const typeName = (typesQ.data ?? []).find((t) => t.id === form.type_id)?.name
        ?? detailQ.data?.type_name;
    const typeColor = (typesQ.data ?? []).find((t) => t.id === form.type_id)?.color
        ?? detailQ.data?.type_color;
    const typeSlug = (typesQ.data ?? []).find((t) => t.id === form.type_id)?.slug
        ?? detailQ.data?.type_slug;

    const switchToBlocks = () => {
        if (isRichBlogHtml(form.body_html)) {
            setMsg({
                type: 'err',
                text: 'Este artículo tiene tablas, FAQ o widgets. Edítalo en HTML: el modo bloques lo rompería.',
            });
            return;
        }
        blockEditor.syncFromHtml(form.body_html);
        setBodyMode('blocks');
    };

    const switchToHtml = () => {
        if (bodyMode === 'blocks') {
            setForm((prev) => ({ ...prev, body_html: blockEditor.toHtml() }));
        }
        setBodyMode('html');
    };

    const onSave = async (publishAnyway = false) => {
        if (!form.title.trim() || !form.slug.trim()) {
            setMsg({ type: 'err', text: 'Título y slug son obligatorios' });
            return;
        }
        const bodyHtml = bodyMode === 'blocks' ? blockEditor.toHtml() : form.body_html;
        if (bodyMode === 'blocks' && isRichBlogHtml(form.body_html)) {
            setMsg({ type: 'err', text: 'No se puede guardar HTML rico desde bloques.' });
            return;
        }
        const leak = patchHasInternalLeak([
            form.title, form.slug, form.excerpt, bodyHtml, form.meta_title, form.meta_description,
        ]);
        if (leak) {
            setMsg({ type: 'err', text: 'Hay notas internas (CHAT/VB/pasada). Quítalas antes de guardar.' });
            return;
        }
        if (textHasDraftMarkers(bodyHtml)) {
            setMsg({ type: 'err', text: 'Quita marcadores de borrador (METADATOS, AIPECES:…) antes de guardar.' });
            return;
        }
        const imageErr = validatePublishRequiresMainImage(form.status, form.main_image_url);
        if (imageErr) {
            setMsg({ type: 'err', text: imageErr });
            return;
        }
        if (shouldAskPublishConfirm(form.status, quality.report.blocking.length, publishAnyway)) {
            setPublishArmed(true);
            setMsg({
                type: 'err',
                text: 'Hay problemas bloqueantes. Corrígelos o pulsa «Publicar igualmente».',
            });
            return;
        }
        if (publishAnyway && form.status === 'enabled' && quality.report.blocking.length > 0) {
            console.warn(
                BLOG_QUALITY_PUBLISH_LOG,
                blogQualityPublishRecord(form.slug.trim(), quality.report.blocking),
            );
        }
        const credit = form.hero_credit_username.trim();
        const payload = {
            p_type_id: form.type_id || null,
            p_slug: form.slug.trim(),
            p_title: form.title.trim(),
            p_excerpt: form.excerpt.trim() || null,
            p_body_html: bodyHtml,
            p_main_image_url: form.main_image_url.trim() || null,
            p_main_image_position: form.main_image_position,
            p_status: form.status,
            p_meta_title: form.meta_title.trim() || null,
            p_meta_description: form.meta_description.trim() || null,
            p_og_image_url: form.og_image_url.trim() || null,
            p_locale: form.locale,
            p_translation_of_id: isNew
                ? form.translation_of_id
                : (form.translation_of_id ?? ''),
            p_hero_credit_username: isNew ? (credit || null) : (credit || ''),
        };
        setMsg(null);
        try {
            if (isNew) {
                const newId = await createMut.mutateAsync(payload);
                setMsg({ type: 'ok', text: 'Borrador creado. Revisa la vista previa antes de publicar.' });
                if (newId && UUID_RE.test(newId)) {
                    navigate(`/blog/posts/${newId}`, { replace: true });
                }
            } else if (validId) {
                await updateMut.mutateAsync({
                    p_id: id,
                    ...payload,
                    p_minor_editorial_change: minorEditorialChange,
                });
                setForm((prev) => ({ ...prev, body_html: bodyHtml }));
                setMinorEditorialChange(false);
                setPublishArmed(false);
                setMsg({ type: 'ok', text: BLOG_SAVE_SUCCESS_MESSAGE });
            }
        } catch (e: unknown) {
            setMsg({ type: 'err', text: errMsg(e) });
        }
    };

    const rewriteBlobImages = () => {
        const source = bodyMode === 'blocks' ? blockEditor.toHtml() : form.body_html;
        const result = rewriteAzureFdImagesToBlob(source);
        if (result.rewritten === 0) return;
        setForm((prev) => ({ ...prev, body_html: result.html }));
        if (bodyMode === 'blocks') setBodyMode('html');
        setMsg({
            type: 'ok',
            text: `Reescritas ${result.rewritten} imágenes al dominio blob. Pulsa Guardar para persistir.`,
        });
    };

    const copyPrompt = async () => {
        const text = buildBlogAiPrompt({
            slug: form.slug || 'nuevo-articulo',
            title: form.title || 'Sin título',
            typeSlug,
            excerpt: form.excerpt,
            metaTitle: form.meta_title,
        });
        await navigator.clipboard.writeText(text);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
    };

    const handleImageSelect = useCallback((url: string, meta?: { creditUsername?: string }) => {
        if (imagePickerTarget === 'main') {
            setForm((f) => ({
                ...f,
                main_image_url: url,
                og_image_url: f.og_image_url || url,
                hero_credit_username:
                    !f.hero_credit_username.trim() && meta?.creditUsername
                        ? meta.creditUsername
                        : f.hero_credit_username,
            }));
        } else {
            blockEditor.updateBlock(imagePickerTarget, { url } as { url: string });
        }
        setShowImagePicker(false);
    }, [imagePickerTarget, blockEditor]);

    const pickTranslation = (item: BlogPostListItem) => {
        setForm((f) => ({ ...f, translation_of_id: item.id }));
        setTranslationLabel(`${item.title} · /${item.slug}`);
        setTranslationSearch('');
    };

    const clearTranslation = () => {
        setForm((f) => ({ ...f, translation_of_id: null }));
        setTranslationLabel('');
        setTranslationSearch('');
    };

    if (!isNew && !validId) {
        return <p className="p-6 text-sm text-destructive">Identificador de entrada no válido.</p>;
    }
    if (!isNew && detailQ.isLoading) {
        return (
            <div className="flex items-center justify-center py-20 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Cargando artículo…
            </div>
        );
    }
    if (!isNew && (detailQ.isError || !detailQ.data)) {
        return (
            <p className="p-6 text-sm text-destructive">
                {(detailQ.error as Error)?.message || 'No se encontró la entrada'}
            </p>
        );
    }

    const pending = createMut.isPending || updateMut.isPending;
    const types = typesQ.data ?? [];

    const editorForm = (
        <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                    <Label htmlFor="bp-type">Tipología</Label>
                    <select
                        id="bp-type"
                        className={inputClass}
                        value={form.type_id ?? ''}
                        onChange={(e) => setForm((f) => ({ ...f, type_id: e.target.value || null }))}
                    >
                        <option value="">Sin tipología</option>
                        {types.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                </div>
                <div className="space-y-1">
                    <Label htmlFor="bp-status">Estado</Label>
                    <select
                        id="bp-status"
                        className={inputClass}
                        value={form.status}
                        onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as BlogPostStatus }))}
                    >
                        {BLOG_STATUS_ORDER.map((s) => (
                            <option key={s} value={s}>{BLOG_STATUS_LABELS[s]}</option>
                        ))}
                    </select>
                    <p className="text-[11px] text-muted-foreground">{BLOG_STATUS_HINTS[form.status]}</p>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                    <Label htmlFor="bp-locale">Idioma</Label>
                    <select
                        id="bp-locale"
                        className={inputClass}
                        value={form.locale}
                        onChange={(e) => {
                            const locale = e.target.value as BlogLocale;
                            setForm((f) => ({
                                ...f,
                                locale,
                                translation_of_id: null,
                            }));
                            setTranslationLabel('');
                            setTranslationSearch('');
                        }}
                    >
                        <option value="es">Español</option>
                        <option value="en">English</option>
                    </select>
                </div>
                <div className="space-y-1">
                    <Label htmlFor="bp-translation">Traducción de</Label>
                    {form.translation_of_id ? (
                        <div className="flex gap-2 items-center">
                            <p className="flex-1 text-sm truncate px-3 py-2 rounded-lg border border-border bg-muted/40">
                                {translationLabel || 'Artículo vinculado'}
                            </p>
                            <Button type="button" variant="outline" size="sm" onClick={clearTranslation}>
                                Limpiar
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            <Input
                                id="bp-translation"
                                value={translationSearch}
                                onChange={(e) => setTranslationSearch(e.target.value)}
                                placeholder={`Buscar artículo en ${opposite === 'en' ? 'English' : 'Español'}…`}
                            />
                            {debouncedTranslationSearch.trim() && (
                                <div className="rounded-lg border border-border max-h-40 overflow-y-auto bg-background">
                                    {translationListQ.isLoading ? (
                                        <p className="px-3 py-2 text-xs text-muted-foreground">Buscando…</p>
                                    ) : (translationListQ.data?.items ?? []).length === 0 ? (
                                        <p className="px-3 py-2 text-xs text-muted-foreground">Sin resultados</p>
                                    ) : (
                                        (translationListQ.data?.items ?? [])
                                            .filter((item) => item.id !== id)
                                            .map((item) => (
                                                <button
                                                    key={item.id}
                                                    type="button"
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted border-b border-border last:border-0"
                                                    onClick={() => pickTranslation(item)}
                                                >
                                                    <span className="font-medium block truncate">{item.title}</span>
                                                    <span className="text-[11px] text-muted-foreground">/{item.slug}</span>
                                                </button>
                                            ))
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="space-y-1">
                <Label htmlFor="bp-title">Título</Label>
                <Input
                    id="bp-title"
                    value={form.title}
                    onChange={(e) => {
                        const v = e.target.value;
                        setForm((f) => ({
                            ...f,
                            title: v,
                            slug: isNew ? slugFromTitle(v) : f.slug,
                            meta_title: f.meta_title || v,
                        }));
                    }}
                />
            </div>
            <div className="space-y-1">
                <Label htmlFor="bp-slug">Slug (URL)</Label>
                <Input id="bp-slug" value={form.slug} onChange={set('slug')} />
            </div>
            <div className="space-y-1">
                <Label htmlFor="bp-excerpt">Resumen</Label>
                <Textarea
                    id="bp-excerpt"
                    rows={2}
                    value={form.excerpt}
                    onChange={(e) => {
                        const v = e.target.value;
                        setForm((f) => ({
                            ...f,
                            excerpt: v,
                            meta_description: f.meta_description || v,
                        }));
                    }}
                />
            </div>
            <div className="space-y-1">
                <Label>Imagen principal</Label>
                <div className="flex gap-2 items-start">
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-muted shrink-0">
                        {form.main_image_url ? (
                            <img src={resolveBlogImageUrl(form.main_image_url)} alt="" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">Sin imagen</div>
                        )}
                    </div>
                    <div className="flex flex-col gap-1">
                        <button
                            type="button"
                            onClick={() => {
                                setImagePickerTarget('main');
                                setShowImagePicker(true);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm hover:bg-muted"
                        >
                            <ImageIcon size={14} />
                            Seleccionar
                        </button>
                        <select
                            className={inputClass}
                            value={form.main_image_position}
                            onChange={(e) => setForm((f) => ({ ...f, main_image_position: e.target.value as 'top' | 'side' }))}
                        >
                            <option value="top">Parte superior (hero)</option>
                            <option value="side">Lateral</option>
                        </select>
                    </div>
                </div>
                <div className="space-y-1 mt-2">
                    <Label htmlFor="bp-hero-credit">Crédito de la portada</Label>
                    <Input
                        id="bp-hero-credit"
                        value={form.hero_credit_username}
                        onChange={set('hero_credit_username')}
                        placeholder="alias del autor"
                    />
                    <p className="text-[11px] text-muted-foreground">
                        Se muestra como «Foto: {form.hero_credit_username.trim() || '…'} · Comunidad AiPeces».
                        {/capture-media/i.test(form.main_image_url)
                            ? ' Si viene de capturas, el alias del pescador suele ser el crédito correcto.'
                            : ''}
                    </p>
                </div>
            </div>

            <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <Label>Cuerpo del artículo</Label>
                    <div className="flex gap-1 text-xs">
                        <button
                            type="button"
                            className={`px-2 py-1 rounded-md border ${bodyMode === 'html' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                            onClick={switchToHtml}
                        >
                            HTML
                        </button>
                        <button
                            type="button"
                            className={`px-2 py-1 rounded-md border ${bodyMode === 'blocks' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'} disabled:opacity-40`}
                            onClick={switchToBlocks}
                            disabled={rich}
                            title={rich ? 'HTML rico: no se puede editar con bloques' : 'Solo artículos simples'}
                        >
                            Bloques
                        </button>
                    </div>
                </div>
                {rich && (
                    <p className="text-[11px] text-amber-800 dark:text-amber-300">
                        Este HTML es rico (tablas, FAQ o widgets). Se guarda tal cual; los bloques lo destruirían.
                    </p>
                )}
                {bodyMode === 'html' ? (
                    <Textarea
                        rows={18}
                        className="font-mono text-xs"
                        value={form.body_html}
                        onChange={set('body_html')}
                        spellCheck={false}
                    />
                ) : (
                    <BlogBlocksPanel
                        blockEditor={blockEditor}
                        onImagePick={(blockId) => {
                            setImagePickerTarget(blockId);
                            setShowImagePicker(true);
                        }}
                        resolveImageUrl={resolveBlogImageUrl}
                    />
                )}
            </div>

            <div className="grid grid-cols-1 gap-3">
                <div className="space-y-1">
                    <Label htmlFor="bp-seo-title">SEO title</Label>
                    <Input id="bp-seo-title" value={form.meta_title} onChange={set('meta_title')} />
                    <p className="text-[10px] text-muted-foreground">{form.meta_title.length}/60</p>
                </div>
                <div className="space-y-1">
                    <Label htmlFor="bp-seo-desc">SEO description</Label>
                    <Textarea id="bp-seo-desc" rows={2} value={form.meta_description} onChange={set('meta_description')} />
                    <p className="text-[10px] text-muted-foreground">{form.meta_description.length}/160</p>
                </div>
                <div className="space-y-1">
                    <Label htmlFor="bp-og">Imagen para redes (og)</Label>
                    <Input id="bp-og" value={form.og_image_url} onChange={set('og_image_url')} />
                </div>
            </div>
        </div>
    );

    const preview = (
        <BlogPostPreview form={previewForm} typeName={typeName} typeColor={typeColor} />
    );

    return (
        <div className="space-y-4" data-page="blog-post-editor" data-blog-id={id} data-blog-slug={form.slug}>
            <PageHeader
                title={form.title || (isNew ? 'Nueva entrada' : 'Entrada de blog')}
                description={form.slug ? publicBlogPath(form.slug, form.locale) : 'Borrador sin slug'}
            >
                <Button type="button" variant="outline" size="sm" onClick={() => navigate('/blog?tab=posts')}>
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Cola
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={copyPrompt}>
                    <Copy className="h-4 w-4 mr-1" />
                    {copied ? 'Prompt copiado' : 'Copiar prompt IA'}
                </Button>
                {form.slug && form.status === 'enabled' && (
                    <a
                        href={publicBlogUrl(form.slug, form.locale)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-md border text-sm hover:bg-muted/50"
                    >
                        <ExternalLink className="h-4 w-4" />
                        Artículo público
                    </a>
                )}
                {BLOG_RETIRE_AND_REDIRECT_ENABLED && validId && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setRetireOpen(true)}>
                        <CornerUpRight className="h-4 w-4 mr-1" />
                        Retirar y redirigir a…
                    </Button>
                )}
                {form.status === 'enabled' && publishArmed && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            setPublishArmed(false);
                            setMsg(null);
                        }}
                    >
                        Seguir editando
                    </Button>
                )}
                {!isNew && (
                    <label className="inline-flex items-center gap-2 text-sm text-muted-foreground mr-1 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            className="rounded border-border"
                            checked={minorEditorialChange}
                            onChange={(e) => setMinorEditorialChange(e.target.checked)}
                        />
                        Cambio menor (no actualizar la fecha)
                    </label>
                )}
                <Button
                    type="button"
                    size="sm"
                    onClick={() => onSave(form.status === 'enabled' && publishArmed)}
                    disabled={pending}
                >
                    {pending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                    {form.status === 'enabled'
                        ? (publishArmed ? 'Publicar igualmente' : 'Publicar')
                        : 'Guardar'}
                </Button>
            </PageHeader>

            <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                    <BlogStatusBadge status={form.status} />
                    <BlogCompleteness flags={flags} />
                </div>
                <BlogQualityPanel
                    report={quality.report}
                    checkingLinks={quality.checkingLinks}
                    publishArmed={publishArmed && form.status === 'enabled'}
                    onRewriteBlobImages={rewriteBlobImages}
                />
            </div>

            {msg && (
                <div
                    className={
                        msg.type === 'ok'
                            ? 'text-sm rounded-md px-3 py-2 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
                            : 'text-sm rounded-md px-3 py-2 bg-destructive/10 text-destructive'
                    }
                >
                    {msg.text}
                </div>
            )}

            <div className="hidden xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(380px,0.95fr)] gap-6 items-start">
                {editorForm}
                <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-auto">{preview}</div>
            </div>

            <div className="xl:hidden">
                <Tabs defaultValue="preview">
                    <TabsList>
                        <TabsTrigger value="preview">Vista previa</TabsTrigger>
                        <TabsTrigger value="edit">Editar</TabsTrigger>
                    </TabsList>
                    <TabsContent value="preview">{preview}</TabsContent>
                    <TabsContent value="edit">{editorForm}</TabsContent>
                </Tabs>
            </div>

            <ImagePickerModal
                isOpen={showImagePicker}
                onClose={() => setShowImagePicker(false)}
                onSelect={handleImageSelect}
                contextTitle={form.title}
            />
            {BLOG_RETIRE_AND_REDIRECT_ENABLED && retireOpen && validId && id && (
                <RetireBlogPostDialog
                    open
                    onOpenChange={setRetireOpen}
                    postId={id}
                    postSlug={form.slug}
                    onRetired={(result) => {
                        setForm((prev) => ({ ...prev, status: 'disabled' }));
                        setPublishArmed(false);
                        setMsg({
                            type: 'ok',
                            text: `Retirado: /blog/${result.old_slug} → /blog/${result.new_slug}. ${BLOG_RETIRE_PIPELINE_WARNING}.`,
                        });
                    }}
                />
            )}
        </div>
    );
}
